/*───────────────────────────────────────────────────────────────────────────*\
  File        : app/pages/auth/LoginPage.tsx
  Description : Combined sign-in / registration page with Google OAuth (GIS),
                plus an e-mail magic-link option.
\*───────────────────────────────────────────────────────────────────────────*/

"use client"

import type React from "react"
import { useState, useEffect, Suspense } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/app/components/Input"
import { Label } from "@/app/components/Label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Separator } from "@/components/ui/separator"
import { Loader2, ArrowLeft } from "lucide-react"
import Link from "next/link"
import { useSession } from "@/app/context/AuthContext"
import { googleOAuth } from "@/src/utils/userAuthApi"
import { MagicLinkModal } from "@/app/components/MagicLinkModal"

/*==========================================================================*
 *                         GOOGLE IDENTITY SERVICES                         *
 *==========================================================================*/

/** Google Client-ID pulled from env. */
const GOOGLE_CLIENT_ID: string = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ""

/**
 * Inject GIS script exactly once.
 */
function useGoogleScript(): void {
  useEffect(() => {
    if (typeof window === "undefined") return
    if (window.google?.accounts?.id) return // already present

    const s: HTMLScriptElement = document.createElement("script")
    s.src = "https://accounts.google.com/gsi/client"
    s.async = true
    s.defer = true
    document.head.appendChild(s)

    return (): void => {
      document.head.removeChild(s)
    }
  }, [])
}

/*==========================================================================*
 *                               PAGE CONTENT                               *
 *==========================================================================*/

function LoginPageContent(): React.JSX.Element {
  /*──────────── URL & context ────────────*/
  const searchParams = useSearchParams()
  const { update } = useSession()
  const router = useRouter()

  /*──────────── Local state ────────────*/
  const [activeTab, setActiveTab] = useState<"login" | "register">(() => {
    const tab = searchParams.get("tab")
    return tab === "create-account" ? "register" : "login"
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(false)
  const [formData, setFormData] = useState<{ email: string; password: string; name: string }>({
    email: "",
    password: "",
    name: "",
  })
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isResendingVerification, setIsResendingVerification] = useState<boolean>(false)

  /*──────────── Magic-link modal ────────────*/
  const [magicOpen, setMagicOpen] = useState<boolean>(false)

  /*──────────── External script ────────────*/
  useGoogleScript()

  /*──────────── GIS initialisation – once per page ────────────*/
  useEffect(() => {
    if (typeof window === "undefined") return
    if (!window.google || !GOOGLE_CLIENT_ID) return

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      ux_mode: "popup", // One-Tap is disabled on http://localhost
      callback: async (resp: { credential: string }): Promise<void> => {
        try {
          const apiResp = await googleOAuth(resp.credential)
          if (apiResp.success && apiResp.user) {
            await update(apiResp.user.id)
            window.location.href = getRedirectUrl()
          } else {
            setError(apiResp.error || "Google sign-in failed.")
          }
        } catch (err) {
          console.error("Google OAuth error:", err)
          setError("Google sign-in failed.")
        } finally {
          setIsGoogleLoading(false)
        }
      },
    })
  }, [])

  /*──────────── Helpers ────────────*/
  const getRedirectUrl = (): string => {
    const redirect = searchParams.get("redirect")
    const billing = searchParams.get("billing")
    if (redirect && billing) return `${redirect}?billing=${billing}`
    if (redirect) return redirect
    return "/dashboard"
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormData((p) => ({ ...p, [name]: value }))
  }

  /*───────────────────────────────────────────────────────────────────────────*
      GOOGLE OAUTH
    *───────────────────────────────────────────────────────────────────────────*/
  const handleGoogleSignIn = (): void => {
    if (process.env.NEXT_PUBLIC_USE_MOCK_REPOS === "true") {
      /* Mock-mode behaves as before */
      setIsGoogleLoading(true)
      setTimeout(() => {
        setIsGoogleLoading(false)
        window.location.href = getRedirectUrl()
      }, 2000)
      return
    }

    /* Safety checks -------------------------------------------------------- */
    if (!window.google || !GOOGLE_CLIENT_ID) {
      setError("Google sign-in not available in this environment.")
      return
    }

    setIsGoogleLoading(true)

    /* Initialise only once ------------------------------------------------- */
    if (!(window as any).__gisInitialised__) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        ux_mode: "popup",
        callback: async (resp: { credential: string }): Promise<void> => {
          try {
            const apiResp = await googleOAuth(resp.credential)
            if (apiResp.success && apiResp.user) {
              await update(apiResp.user.id)
              window.location.href = getRedirectUrl()
            } else {
              setError(apiResp.error || "Google sign-in failed.")
            }
          } catch (err) {
            console.error("Google OAuth error:", err)
            setError("Google sign-in failed.")
          } finally {
            setIsGoogleLoading(false)
          }
        },
      })
      ;(window as any).__gisInitialised__ = true
    }

    /* Now show the popup --------------------------------------------------- */
    window.google.accounts.id.prompt((n) => {
      if (n.isNotDisplayed() || n.isSkippedMoment()) {
        console.log("[GIS] prompt dismissed:", n.getNotDisplayedReason?.() || n.getSkippedReason?.())
        setIsGoogleLoading(false)
      }
    })
  }

  /*───────────────────────────────────────────────────────────────────────────*
      EMAIL / PASSWORD FLOW
    *───────────────────────────────────────────────────────────────────────────*/
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)
    setSuccessMessage(null)

    try {
      if (activeTab === "login") {
        /* Login */
        const res = await fetch("/api/user_authentication/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: formData.email, password: formData.password }),
        })
        const result = await res.json()
        if (result.success) {
          await update(result.user.id)
          setTimeout(() => {
            window.location.href = getRedirectUrl()
          }, 100)
        } else {
          setError(result.error || "Login failed.")
        }
      } else {
        /* Registration */
        const res = await fetch("/api/user_authentication/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            password: formData.password,
            plan: "free",
          }),
        })
        const result = await res.json()
        if (result.success) {
          setSuccessMessage(
            "Registration successful! Please check your email to verify your account before signing in.",
          )
          setFormData({ email: "", password: "", name: "" })
        } else {
          setError(result.error || "Registration failed.")
        }
      }
    } catch (err) {
      console.error("Auth error:", err)
      setError("An error occurred during authentication.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendVerification = async (): Promise<void> => {
    if (!formData.email) {
      setError("Please enter your email address first.")
      return
    }

    setIsResendingVerification(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const res = await fetch("/api/user_authentication/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      })
      const result = await res.json()

      if (result.success) {
        setSuccessMessage("Verification email sent! Please check your inbox.")
      } else {
        setError(result.error || "Failed to resend verification email.")
      }
    } catch (err) {
      console.error("Resend verification error:", err)
      setError("An error occurred while resending the verification email.")
    } finally {
      setIsResendingVerification(false)
    }
  }

  /*───────────────────────────────────────────────────────────────────────────*
      JSX RENDER
    *───────────────────────────────────────────────────────────────────────────*/
  return (
    <div className="container px-4 py-8 mx-auto md:px-6 md:py-12">
      <div className="max-w-2xl mx-auto">
        {/* Back to Home */}
        <div className="mb-6">
          <Link
            href="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-[#40A1AB] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome to Roomvia</h1>
          <p className="text-gray-600">Sign in to your account or create a new one to get started</p>
        </div>

        <Card>
          <CardContent className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Sign In</TabsTrigger>
                <TabsTrigger value="register">Create Account</TabsTrigger>
              </TabsList>

              {/*──────────────────── SIGN-IN TAB ────────────────────*/}
              <TabsContent value="login">
                <CardHeader className="px-0 pt-0">
                  <CardTitle>Sign In</CardTitle>
                  <CardDescription>Enter your email and password to access your account</CardDescription>
                </CardHeader>

                <div className="space-y-4">
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-600">{error}</p>
                      {error.toLowerCase().includes("verify") &&
                        (error.toLowerCase().includes("email") || error.toLowerCase().includes("e-mail")) && (
                          <Button
                            type="button"
                            variant="link"
                            className="mt-2 p-0 h-auto text-[#40A1AB] hover:text-[#3A919A]"
                            onClick={handleResendVerification}
                            disabled={isResendingVerification}
                          >
                            {isResendingVerification ? (
                              <>
                                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              "Resend Verification Email"
                            )}
                          </Button>
                        )}
                    </div>
                  )}

                  {successMessage && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <p className="text-sm text-green-600">{successMessage}</p>
                    </div>
                  )}

                  {/* Google Sign-In */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full bg-transparent"
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading || isLoading}
                  >
                    {isGoogleLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in with Google...
                      </>
                    ) : (
                      <>
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        Continue with Google
                      </>
                    )}
                  </Button>

                  {/* Magic-link button */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full bg-transparent"
                    onClick={() => setMagicOpen(true)}
                    disabled={isLoading || isGoogleLoading}
                  >
                    Email me a magic link
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <Separator className="w-full" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid gap-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Link href="/forgot-password" className="text-sm text-[#40A1AB] hover:underline">
                        Forgot password?
                      </Link>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#40A1AB] hover:bg-[#3A919A] text-white"
                      disabled={isLoading || isGoogleLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </Button>

                    <div className="text-center">
                      <p className="text-sm text-gray-600">
                        or{" "}
                        <button
                          type="button"
                          onClick={() => setActiveTab("register")}
                          className="text-[#40A1AB] hover:underline font-medium"
                        >
                          Register
                        </button>
                      </p>
                    </div>
                  </form>
                </div>
              </TabsContent>

              {/*──────────────────── REGISTER TAB ────────────────────*/}
              <TabsContent value="register">
                <CardHeader className="px-0 pt-0">
                  <CardTitle>Create Your Account</CardTitle>
                  <CardDescription>Enter your details to get started with your free account</CardDescription>
                </CardHeader>

                <div className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  {successMessage && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <p className="text-sm text-green-600">{successMessage}</p>
                    </div>
                  )}

                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <p className="text-sm text-green-800">
                      <strong>Choose your preferred sign-in method:</strong> You can create your account using Google,
                      Magic Link, or Email/Password. Each email address can only be associated with one account, so
                      please choose the method you'd like to use for all future logins.
                    </p>
                  </div>

                  {/* Google sign-up */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full bg-transparent"
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading || isLoading}
                  >
                    {isGoogleLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account with Google...
                      </>
                    ) : (
                      <>
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        Continue with Google
                      </>
                    )}
                  </Button>

                  {/* Magic-link button */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full bg-transparent"
                    onClick={() => setMagicOpen(true)}
                    disabled={isLoading || isGoogleLoading}
                  >
                    Email me a magic link
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <Separator className="w-full" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="email-register">Email</Label>
                      <Input
                        id="email-register"
                        name="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="password-register">Password</Label>
                      <Input
                        id="password-register"
                        name="password"
                        type="password"
                        placeholder="Create a strong password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="text-xs text-gray-600">
                      By creating an account, you agree to our{" "}
                      <Link href="/legal/terms" className="text-[#40A1AB] hover:underline">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link href="/legal/privacy" className="text-[#40A1AB] hover:underline">
                        Privacy Policy
                      </Link>
                      .
                    </div>

                    {/* Simplified button text */}
                    <Button
                      type="submit"
                      className="w-full bg-[#40A1AB] hover:bg-[#3A919A] text-white"
                      disabled={isLoading || isGoogleLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        "Create Free Account"
                      )}
                    </Button>
                  </form>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>
            Need help?{" "}
            <Link href="/support" className="text-[#40A1AB] hover:underline">
              Contact Support
            </Link>
          </p>
        </div>
      </div>

      {/* Magic-link modal */}
      <MagicLinkModal open={magicOpen} onOpenChange={setMagicOpen} />
    </div>
  )
}

/*───────────────────────────────────────────────────────────────────────────*
  Suspense wrapper
*───────────────────────────────────────────────────────────────────────────*/
function LoginPageFallback(): React.JSX.Element {
  return (
    <div className="container px-4 py-8 mx-auto md:px-6 md:py-12">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Loading...</h1>
          <p className="text-gray-600">Preparing login page</p>
        </div>
      </div>
    </div>
  )
}

export function LoginPage(): React.JSX.Element {
  return (
    <Suspense fallback={<LoginPageFallback />}>
      <LoginPageContent />
    </Suspense>
  )
}

/*──────────────────────────── End of file ────────────────────────────────*/
